import { useRef } from "react";
import {
  FileText,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Car,
  Wrench,
  Scale,
  CircleDollarSign,
  ClipboardList,
  Calendar,
  Hash,
} from "lucide-react";
import { ExportControls } from "./ExportControls";

interface ReportViewerProps {
  data: any;
  onNewAnalysis?: () => void;
}

export function ReportViewer({ data, onNewAnalysis }: ReportViewerProps) {
  const reportRef = useRef<HTMLDivElement>(null);

  if (!data) {
    return (
      <div className="bg-white rounded-xl shadow-lg p-8 text-center">
        <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
        <p className="text-gray-500">Rapor verisi bulunamadı</p>
      </div>
    );
  }

  const getSeverityColor = (severity: string) => {
    switch (severity?.toLowerCase()) {
      case "hafif":
      case "düşük":
      case "low":
        return "text-green-600 bg-green-50 border-green-200";
      case "orta":
      case "medium":
        return "text-yellow-600 bg-yellow-50 border-yellow-200";
      case "ağır":
      case "yüksek":
      case "high":
        return "text-red-600 bg-red-50 border-red-200";
      default:
        return "text-gray-600 bg-gray-50 border-gray-200";
    }
  };

  const getSeverityIcon = (severity: string) => {
    switch (severity?.toLowerCase()) {
      case "hafif":
      case "düşük":
      case "low":
        return <CheckCircle className="w-5 h-5" />;
      case "orta":
      case "medium":
        return <AlertTriangle className="w-5 h-5" />;
      case "ağır":
      case "yüksek":
      case "high":
        return <XCircle className="w-5 h-5" />;
      default:
        return <AlertTriangle className="w-5 h-5" />;
    }
  };

  // Deep search for license plate in any nested structure
  const getLicensePlate = (obj: any, depth = 0): string | null => {
    if (!obj || depth > 5) return null;

    // Check common field names
    const plateFields = [
      "licensePlate",
      "license_plate",
      "plateNumber",
      "plate_number",
      "plaka",
      "plate",
    ];
    for (const field of plateFields) {
      if (obj[field] && typeof obj[field] === "string") {
        return obj[field];
      }
    }

    // If it's an array, check first element
    if (Array.isArray(obj) && obj.length > 0) {
      const found = getLicensePlate(obj[0], depth + 1);
      if (found) return found;
    }

    // Search in nested objects
    if (typeof obj === "object") {
      for (const key of Object.keys(obj)) {
        if (typeof obj[key] === "object") {
          const found = getLicensePlate(obj[key], depth + 1);
          if (found) return found;
        }
      }
    }

    return null;
  };

  const faultPercentage = data.faultAnalysis?.faultPercentage || 0;
  const totalCost = data.costEstimation?.totalCost || 0;

  return (
    <div className="space-y-6">
      {/* Header with Export Controls */}
      <div className="bg-white rounded-xl shadow-lg p-6 print:shadow-none">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
              <Scale className="w-8 h-8 text-indigo-600" />
              Sigorta Hasar Analiz Raporu
            </h1>
            <p className="text-gray-500 mt-1">
              Detaylı hasar değerlendirmesi ve maliyet analizi
            </p>
          </div>
          <div className="print:hidden">
            <ExportControls reportData={data} reportRef={reportRef} />
          </div>
        </div>
      </div>

      {/* Printable Report Content */}
      <div ref={reportRef} className="space-y-6 print:space-y-4">
        {/* Report Metadata */}
        <div className="bg-white rounded-xl shadow-lg p-6 print:shadow-none print:border print:border-gray-200">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-indigo-50 rounded-lg">
                <Hash className="w-5 h-5 text-indigo-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide">
                  Rapor No
                </p>
                <p className="font-semibold text-gray-900">
                  {data.reportId || "—"}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-indigo-50 rounded-lg">
                <Calendar className="w-5 h-5 text-indigo-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide">
                  Tarih
                </p>
                <p className="font-semibold text-gray-900">
                  {data.generatedAt
                    ? new Date(data.generatedAt).toLocaleDateString("tr-TR", {
                        day: "2-digit",
                        month: "long",
                        year: "numeric",
                      })
                    : "—"}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-indigo-50 rounded-lg">
                <FileText className="w-5 h-5 text-indigo-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide">
                  Durum
                </p>
                <p className="font-semibold text-green-600">Tamamlandı</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-indigo-50 rounded-lg">
                <Car className="w-5 h-5 text-indigo-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide">
                  Plaka No
                </p>
                <p className="font-semibold text-gray-900 text-sm">
                  {getLicensePlate(data) || "—"}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 print:grid-cols-3">
          {/* Fault Percentage Card */}
          <div className="bg-white rounded-xl shadow-lg p-6 print:shadow-none print:border print:border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide">
                Kusur Oranı
              </h3>
              <Scale className="w-5 h-5 text-indigo-600" />
            </div>
            <div className="flex items-end gap-2">
              <span className="text-4xl font-bold text-gray-900">
                %{faultPercentage}
              </span>
            </div>
            <div className="mt-3 h-2 bg-gray-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-indigo-600 rounded-full transition-all duration-500"
                style={{ width: `${faultPercentage}%` }}
              />
            </div>
          </div>

          {/* Damage Severity Card */}
          <div className="bg-white rounded-xl shadow-lg p-6 print:shadow-none print:border print:border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide">
                Hasar Şiddeti
              </h3>
              <AlertTriangle className="w-5 h-5 text-amber-600" />
            </div>
            <div
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg border ${getSeverityColor(
                data.damageAssessment?.severity
              )}`}
            >
              {getSeverityIcon(data.damageAssessment?.severity)}
              <span className="font-semibold capitalize">
                {data.damageAssessment?.severity || "Belirtilmemiş"}
              </span>
            </div>
            <p className="mt-3 text-sm text-gray-500">
              Onarılabilirlik:{" "}
              <span className="font-medium text-gray-700">
                {data.damageAssessment?.repairability || "—"}
              </span>
            </p>
          </div>

          {/* Total Cost Card */}
          <div className="bg-white rounded-xl shadow-lg p-6 print:shadow-none print:border print:border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide">
                Tahmini Maliyet
              </h3>
              <CircleDollarSign className="w-5 h-5 text-green-600" />
            </div>
            <div className="flex items-baseline gap-1">
              <span className="text-3xl font-bold text-gray-900">
                ₺{totalCost.toLocaleString("tr-TR")}
              </span>
            </div>
            <p className="mt-3 text-sm text-gray-500">
              Toplam tahmini onarım maliyeti
            </p>
          </div>
        </div>

        {/* Detailed Sections */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 print:grid-cols-2 print:gap-4">
          {/* Fault Analysis Section */}
          <div className="bg-white rounded-xl shadow-lg p-6 print:shadow-none print:border print:border-gray-200">
            <div className="flex items-center gap-3 mb-4 pb-3 border-b">
              <div className="p-2 bg-indigo-100 rounded-lg">
                <Scale className="w-5 h-5 text-indigo-600" />
              </div>
              <h2 className="text-lg font-bold text-gray-900">Kusur Analizi</h2>
            </div>
            <div className="space-y-4">
              <div>
                <h4 className="text-sm font-medium text-gray-500 mb-1">
                  Değerlendirme
                </h4>
                <p className="text-gray-700 leading-relaxed">
                  {data.faultAnalysis?.determination ||
                    "Değerlendirme yapılmadı."}
                </p>
              </div>
              {data.faultAnalysis?.reasoning && (
                <div>
                  <h4 className="text-sm font-medium text-gray-500 mb-1">
                    Gerekçe
                  </h4>
                  <p className="text-gray-700 leading-relaxed">
                    {data.faultAnalysis.reasoning}
                  </p>
                </div>
              )}
              {data.faultAnalysis?.legalBasis && (
                <div className="bg-indigo-50 rounded-lg p-3 border border-indigo-100">
                  <h4 className="text-sm font-medium text-indigo-700 mb-1">
                    Yasal Dayanak
                  </h4>
                  <p className="text-indigo-900 text-sm">
                    {data.faultAnalysis.legalBasis}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Damage Assessment Section */}
          <div className="bg-white rounded-xl shadow-lg p-6 print:shadow-none print:border print:border-gray-200">
            <div className="flex items-center gap-3 mb-4 pb-3 border-b">
              <div className="p-2 bg-amber-100 rounded-lg">
                <Car className="w-5 h-5 text-amber-600" />
              </div>
              <h2 className="text-lg font-bold text-gray-900">
                Hasar Değerlendirmesi
              </h2>
            </div>
            <div className="space-y-4">
              {data.damageAssessment?.description && (
                <div>
                  <h4 className="text-sm font-medium text-gray-500 mb-1">
                    Açıklama
                  </h4>
                  <p className="text-gray-700 leading-relaxed">
                    {data.damageAssessment.description}
                  </p>
                </div>
              )}
              {data.damageAssessment?.areas &&
                data.damageAssessment.areas.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-500 mb-2">
                      Hasarlı Bölgeler
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {data.damageAssessment.areas.map(
                        (area: string, idx: number) => (
                          <span
                            key={idx}
                            className="px-3 py-1 bg-amber-50 text-amber-700 rounded-full text-sm border border-amber-200"
                          >
                            {area}
                          </span>
                        )
                      )}
                    </div>
                  </div>
                )}
            </div>
          </div>
        </div>

        {/* Cost Breakdown Section */}
        <div className="bg-white rounded-xl shadow-lg p-6 print:shadow-none print:border print:border-gray-200">
          <div className="flex items-center gap-3 mb-4 pb-3 border-b">
            <div className="p-2 bg-green-100 rounded-lg">
              <Wrench className="w-5 h-5 text-green-600" />
            </div>
            <h2 className="text-lg font-bold text-gray-900">Maliyet Dökümü</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500 uppercase tracking-wide">
                    Kalem
                  </th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-gray-500 uppercase tracking-wide">
                    Tutar
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                <tr>
                  <td className="py-3 px-4 text-gray-700">İşçilik Maliyeti</td>
                  <td className="py-3 px-4 text-right font-medium text-gray-900">
                    ₺
                    {(data.costEstimation?.laborCost || 0).toLocaleString(
                      "tr-TR"
                    )}
                  </td>
                </tr>
                <tr>
                  <td className="py-3 px-4 text-gray-700">Parça Maliyeti</td>
                  <td className="py-3 px-4 text-right font-medium text-gray-900">
                    ₺
                    {(data.costEstimation?.partsCost || 0).toLocaleString(
                      "tr-TR"
                    )}
                  </td>
                </tr>
                <tr>
                  <td className="py-3 px-4 text-gray-700">Boya ve Kaplama</td>
                  <td className="py-3 px-4 text-right font-medium text-gray-900">
                    ₺
                    {(data.costEstimation?.paintCost || 0).toLocaleString(
                      "tr-TR"
                    )}
                  </td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="py-4 px-4 font-bold text-gray-900">TOPLAM</td>
                  <td className="py-4 px-4 text-right font-bold text-xl text-indigo-600">
                    ₺{totalCost.toLocaleString("tr-TR")}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Recommendations Section */}
        {data.recommendations && data.recommendations.length > 0 && (
          <div className="bg-white rounded-xl shadow-lg p-6 print:shadow-none print:border print:border-gray-200">
            <div className="flex items-center gap-3 mb-4 pb-3 border-b">
              <div className="p-2 bg-blue-100 rounded-lg">
                <ClipboardList className="w-5 h-5 text-blue-600" />
              </div>
              <h2 className="text-lg font-bold text-gray-900">
                Öneriler ve Sonuç
              </h2>
            </div>
            <ul className="space-y-3">
              {data.recommendations.map((rec: string, idx: number) => (
                <li key={idx} className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-sm font-medium">
                    {idx + 1}
                  </span>
                  <p className="text-gray-700 leading-relaxed">{rec}</p>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Footer */}
        <div className="text-center py-4 text-sm text-gray-400 print:border-t print:mt-8">
          <p>
            Bu rapor Insura AI sistemi tarafından otomatik olarak
            oluşturulmuştur.
          </p>
          <p className="mt-1">
            Oluşturulma: {new Date().toLocaleString("tr-TR")} | Rapor No:{" "}
            {data.reportId || "—"}
          </p>
        </div>
      </div>

      {/* New Analysis Button */}
      {onNewAnalysis && (
        <div className="flex justify-center print:hidden">
          <button
            onClick={onNewAnalysis}
            className="btn btn-outline px-8 py-3 text-lg"
          >
            Yeni Analiz Başlat
          </button>
        </div>
      )}
    </div>
  );
}
